import { FoodItem } from "@/packages/dashboard/api/get-meals";
import { getFoodItems } from "@/packages/dashboard/api/get-food-items";
import { Box, Button, Loader, NumberInput, Paper, Stack, Table, Text, TextInput, Title, rem, rgba, useMantineTheme } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";

export function FoodItems() {
  const theme = useMantineTheme();
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFoodItems()
      .then((response) => {
        setFoodItems(response.food_items);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const surfaceBorder = `1px solid ${rgba(theme.black, 0.08)}`;

  const handleUpdate = (id: number, field: keyof FoodItem, value: string | number) => {
    setFoodItems((items) =>
      items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleDelete = (id: number) => {
    setFoodItems((items) => items.filter((item) => item.id !== id));
  };

  if (loading) {
    return (
      <Stack gap="xl" p="xl" align="center" justify="center" style={{ minHeight: "50vh" }}>
        <Loader size="lg" color="#0F172A" />
        <Text c="#1F2933" size="sm">Loading food items...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="xl" p="xl">
        <Box>
          <Title order={1} fw={700} c="#B9382C" style={{ letterSpacing: rem(1.2), fontSize: rem(32) }}>
            ERROR
          </Title>
          <Text c="#1F2933" size="sm" mt="xs" style={{ lineHeight: 1.6 }}>
            {error}
          </Text>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap="xl" p="xl">
      <Box>
        <Title
          order={1}
          fw={700}
          c="#111827"
          style={{ letterSpacing: rem(1.2), fontSize: rem(32) }}
        >
          FOOD ITEMS
        </Title>
        <Text c="#1F2933" size="sm" mt="xs" style={{ lineHeight: 1.6 }}>
          Manage your food database. Click on any cell to edit.
        </Text>
      </Box>

      <Paper
        p="lg"
        radius="md"
        style={{
          border: surfaceBorder,
          background: theme.white,
          boxShadow: "0 12px 24px -20px rgba(15, 23, 42, 0.35)",
        }}
      >
        <Stack gap="md">
          <Box style={{ overflowX: "auto" }}>
            <Table
              highlightOnHover
              withTableBorder={false}
              withColumnBorders={false}
              styles={{
                th: {
                  textTransform: "uppercase",
                  letterSpacing: rem(1.2),
                  fontSize: rem(11),
                  fontWeight: 700,
                  color: "#111827",
                  borderBottom: surfaceBorder,
                  paddingTop: rem(12),
                  paddingBottom: rem(12),
                },
                td: {
                  borderBottom: `1px solid ${rgba(theme.black, 0.05)}`,
                  paddingTop: rem(12),
                  paddingBottom: rem(12),
                  color: "#1F2933",
                },
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Size</Table.Th>
                  <Table.Th>Unit</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Calories</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Protein (g)</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Carbs (g)</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Fat (g)</Table.Th>
                  <Table.Th>Serving</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {foodItems.map((item) => {
                  const isEditing = editingId === item.id;
                  return (
                    <Table.Tr
                      key={item.id}
                      onClick={() => setEditingId(item.id)}
                      style={{
                        cursor: "pointer",
                        background: isEditing ? rgba(theme.black, 0.02) : "transparent",
                      }}
                    >
                      <Table.Td>
                        <TextInput
                          value={item.name}
                          onChange={(e) => handleUpdate(item.id, "name", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          styles={{
                            input: {
                              fontWeight: 600,
                              color: "#1F2933",
                            },
                          }}
                        />
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <TextInput
                          value={item.serving_size}
                          onChange={(e) => handleUpdate(item.id, "serving_size", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          style={{ width: rem(60) }}
                          styles={{ input: { textAlign: "right" } }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="#1F2933">
                          {item.serving_unit}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <NumberInput
                          value={item.calories}
                          onChange={(val) => handleUpdate(item.id, "calories", Number(val) || 0)}
                          variant="unstyled"
                          size="sm"
                          hideControls
                          style={{ width: rem(70) }}
                          styles={{ input: { textAlign: "right" } }}
                        />
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <TextInput
                          value={item.proteins}
                          onChange={(e) => handleUpdate(item.id, "proteins", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          style={{ width: rem(60) }}
                          styles={{ input: { textAlign: "right" } }}
                        />
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <TextInput
                          value={item.carbs}
                          onChange={(e) => handleUpdate(item.id, "carbs", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          style={{ width: rem(60) }}
                          styles={{ input: { textAlign: "right" } }}
                        />
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <TextInput
                          value={item.fats}
                          onChange={(e) => handleUpdate(item.id, "fats", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          style={{ width: rem(60) }}
                          styles={{ input: { textAlign: "right" } }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          value={item.serving_description || ""}
                          onChange={(e) => handleUpdate(item.id, "serving_description", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          placeholder="e.g., one cup"
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          value={item.description || ""}
                          onChange={(e) => handleUpdate(item.id, "description", e.currentTarget.value)}
                          variant="unstyled"
                          size="sm"
                          placeholder="Add description"
                        />
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="subtle"
                          size="xs"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                        >
                          <IconTrash size={16} />
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
